import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import Percent from "_shared/percentages/percent";
import { IRedlineableField, JSONType } from "work/proposals/redlining/field-redlines/field-redline";
import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import ProposalFieldSchema, { ProposalFieldJSON } from "./_schema/proposal-field-schema";

export const ProposalFieldCategory = {
  Details: "Details",
  Team: "Team",
  FeeSchedule: "Fee Schedule",
  Conflicts: "Conflicts",
  Policies: "Policies",
  Discount: "Discount",
} as const;

export const ProposalFieldName = {
  General: "general",
  Client: "client",
  Name: "name",
  Description: "description",
  Negotiable: "negotiable",
  ResponseDueBy: "responseDueBy",
  StartDate: "startDate",
  EndDate: "endDate",
  TeamLeader: "teamLeader",
  Team: "team",
  FeeSchedule: "feeSchedule",
  WaiveConflictsCheck: "waiveConflictsCheck",
  TeamRestriction: "teamRestriction",
  Conflicts: "conflicts",
  Policies: "policies",
  ClientPolicies: "clientPolicies",
  VendorPolicies: "vendorPolicies",
  Discount: "discount",
} as const;

/**
 * Non-exported so no value outside this module can produce a {@link ProposalFieldTimelineIdentity} —
 * the only way to get one is `ProposalField.timelineIdentity()` (or the `scalarWithIdTimelineIdentity`
 * escape hatch below, for the one caller — `ProposalFieldTimelineDropdown`'s `scalarPath` prop — that
 * still needs to force the structured-scalar case explicitly).
 */
const proposalFieldTimelineIdentityBrand = Symbol("ProposalFieldTimelineIdentity");

/**
 * A field's identity for `ProposalFieldTimelineDropdown`'s revert-history mechanism: a plain scalar
 * (Name, a date, …); a *scalar* whose `ProposalField` happens to carry an id for comment/history
 * scoping only (the vendor-team *leader* — `/vendorTeam/leader` is one value, not a collection); or a
 * genuine collection item (a vendor-team *member*, a fee-schedule category, a document, a policy).
 *
 * ROADMAP.md's standing constraint — "the team leader is a scalar path despite carrying an id" — used
 * to be enforced only by a caller remembering to pass `scalarPath` to the dropdown; forgetting it lets
 * the dropdown derive an itemId from the leader's own id, matching no event in the stream and rendering
 * a permanently empty revert menu. `ProposalField.timelineIdentity()` is now the one place that decides
 * which of these three a field is, so no call site chooses (or mis-chooses) it independently, and the
 * branded return type here cannot be fabricated by hand outside this module — it can only ever come
 * from a `ProposalField` answering for itself (or the escape hatch below, for the one legitimate
 * caller-supplied case).
 */
export type ProposalFieldTimelineIdentity =
  | { readonly kind: "scalar"; readonly [proposalFieldTimelineIdentityBrand]: true }
  | { readonly kind: "scalarWithId"; readonly path: string; readonly [proposalFieldTimelineIdentityBrand]: true }
  | { readonly kind: "collectionItem"; readonly itemId: string; readonly [proposalFieldTimelineIdentityBrand]: true };

/**
 * Known structured-scalar paths a caller may explicitly force via `ProposalFieldTimelineDropdown`'s
 * `scalarPath` prop. Closed (rather than `string`) so a typo'd path fails to compile instead of silently
 * querying a path with no history.
 */
export type ProposalStructuredScalarPath = "/vendorTeam/leader";

/**
 * The only way to build a `scalarWithId` {@link ProposalFieldTimelineIdentity} without going through a
 * specific `ProposalField` instance's own {@link ProposalField.timelineIdentity}. Exists solely for
 * `ProposalFieldTimelineDropdown`'s `scalarPath` prop (a caller-supplied override for a structured
 * scalar not yet known to `timelineIdentity` itself) — kept next to the brand so the brand symbol never
 * has to leave this module.
 */
export function scalarWithIdTimelineIdentity(path: ProposalStructuredScalarPath): ProposalFieldTimelineIdentity {
  return { kind: "scalarWithId", path, [proposalFieldTimelineIdentityBrand]: true };
}

export type ProposalFieldCategoryType = (typeof ProposalFieldCategory)[keyof typeof ProposalFieldCategory];
export type ProposalFieldNameValue = (typeof ProposalFieldName)[keyof typeof ProposalFieldName];
export enum HumanReadableProposalFieldName {
  client = "Client",
  name = "Name",
  description = "Description",
  negotiable = "Negotiable",
  responseDueBy = "Response Due By",
  startDate = "Start Date",
  endDate = "End Date",
  teamLeader = "Team Leader",
  team = "Team",
  feeSchedule = "Fee Schedule",
  waiveConflictsCheck = "Waive Conflicts Check",
  teamRestriction = "Team Restriction",
  conflicts = "Conflicts",
  policies = "Policies",
  clientPolicies = "Client Policies",
  vendorPolicies = "Vendor Policies",
  discount = "Discount",
  general = "General",
}

export default class ProposalField {
  public static readonly General: ProposalField = new ProposalField({
    name: ProposalFieldName.General,
    category: ProposalFieldCategory.Details,
    proposalJSONKeys: [],
  });
  public static readonly Client: ProposalField = new ProposalField({
    name: ProposalFieldName.Client,
    category: ProposalFieldCategory.Details,
    proposalJSONKeys: [],
  });
  public static readonly Name: ProposalField = new ProposalField({
    name: ProposalFieldName.Name,
    category: ProposalFieldCategory.Details,
    fromJSON: ProjectName.fromJSON,
  });
  public static readonly Description: ProposalField = new ProposalField({
    name: ProposalFieldName.Description,
    category: ProposalFieldCategory.Details,
    fromJSON: ProjectDescription.fromJSON,
  });
  public static readonly Negotiable: ProposalField = new ProposalField({
    name: ProposalFieldName.Negotiable,
    category: ProposalFieldCategory.Details,
    fromJSON: AHBoolean.fromJSON,
    proposalJSONKeys: [],
  });
  public static readonly ResponseDueBy: ProposalField = new ProposalField({
    name: ProposalFieldName.ResponseDueBy,
    category: ProposalFieldCategory.Details,
    fromJSON: AHDate.fromUnknownJSON,
  });
  public static readonly StartDate: ProposalField = new ProposalField({
    name: ProposalFieldName.StartDate,
    category: ProposalFieldCategory.Details,
    fromJSON: AHDate.fromUnknownJSON,
  });
  public static readonly EndDate: ProposalField = new ProposalField({
    name: ProposalFieldName.EndDate,
    category: ProposalFieldCategory.Details,
    fromJSON: AHDate.fromUnknownJSON,
  });
  public static readonly Team: ProposalField = new ProposalField({
    name: ProposalFieldName.Team,
    category: ProposalFieldCategory.Team,
    fromJSON: EntityMember.fromUnknownJSON,
    proposalJSONKeys: ["vendorTeam"],
  });
  public static readonly TeamRestriction: ProposalField = new ProposalField({
    name: ProposalFieldName.TeamRestriction,
    category: ProposalFieldCategory.Team,
    fromJSON: AHBoolean.fromJSON,
    commentField: ProposalField.Team,
    proposalJSONKeys: ["teamRestricted"],
  });
  public static readonly FeeSchedule: ProposalField = new ProposalField({
    name: ProposalFieldName.FeeSchedule,
    category: ProposalFieldCategory.FeeSchedule,
    fromJSON: FeeScheduleCategory.fromUnknownJSON,
  });
  public static readonly Conflicts: ProposalField = new ProposalField({
    name: ProposalFieldName.Conflicts,
    category: ProposalFieldCategory.Conflicts,
    fromJSON: WorkDocument.fromUnknownJSON,
  });
  public static readonly WaiveConflictsCheck: ProposalField = new ProposalField({
    name: ProposalFieldName.WaiveConflictsCheck,
    category: ProposalFieldCategory.Conflicts,
    fromJSON: AHBoolean.fromJSON,
    commentField: ProposalField.Conflicts,
    proposalJSONKeys: ["conflictsCheckWaived"],
  });
  public static readonly Policies: ProposalField = new ProposalField({
    name: ProposalFieldName.Policies,
    category: ProposalFieldCategory.Policies,
    fromJSON: WorkDocument.fromUnknownJSON,
  });
  public static readonly ClientPolicies: ProposalField = new ProposalField({
    name: ProposalFieldName.ClientPolicies,
    category: ProposalFieldCategory.Policies,
    fromJSON: WorkDocument.fromUnknownJSON,
    unresolvedFieldNames: [ProposalFieldName.ClientPolicies, ProposalFieldName.Policies],
  });
  public static readonly VendorPolicies: ProposalField = new ProposalField({
    name: ProposalFieldName.VendorPolicies,
    category: ProposalFieldCategory.Policies,
    fromJSON: WorkDocument.fromUnknownJSON,
    unresolvedFieldNames: [ProposalFieldName.VendorPolicies, ProposalFieldName.Policies],
  });
  public static readonly Discount: ProposalField = new ProposalField({
    name: ProposalFieldName.Discount,
    category: ProposalFieldCategory.Discount,
    fromJSON: Percent.fromUnknownJSON,
  });
  public static readonly CounterRevisionFields: readonly ProposalField[] = [
    ProposalField.Name,
    ProposalField.Description,
    ProposalField.ResponseDueBy,
    ProposalField.StartDate,
    ProposalField.EndDate,
    ProposalField.Team,
    ProposalField.TeamRestriction,
    ProposalField.FeeSchedule,
    ProposalField.ClientPolicies,
    ProposalField.VendorPolicies,
    ProposalField.Conflicts,
    ProposalField.WaiveConflictsCheck,
    ProposalField.Discount,
  ];

  public static fromJSON(json?: unknown): ProposalField | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const params = ProposalFieldSchema.deserialize(json);
    if (params === "general") return ProposalField.General;

    return new ProposalField(params);
  }

  public static TeamLeader(id?: Guid): ProposalField {
    return new ProposalField({
      name: ProposalFieldName.TeamLeader,
      category: ProposalFieldCategory.Team,
      id,
      commentField: ProposalField.Team,
    });
  }

  public static TeamMember(id: Guid): ProposalField {
    return new ProposalField({
      name: ProposalFieldName.Team,
      category: ProposalFieldCategory.Team,
      id,
      commentField: ProposalField.Team,
    });
  }

  public static FeeScheduleCategory(id: Guid): ProposalField {
    return new ProposalField({
      name: ProposalFieldName.FeeSchedule,
      category: ProposalFieldCategory.FeeSchedule,
      id,
    });
  }

  public static ConflictsDocument(id: Guid): ProposalField {
    return new ProposalField({
      name: ProposalFieldName.Conflicts,
      category: ProposalFieldCategory.Conflicts,
      id,
      commentField: ProposalField.Conflicts,
    });
  }

  public static ClientPolicy(id: Guid): ProposalField {
    return new ProposalField({
      name: ProposalFieldName.ClientPolicies,
      category: ProposalFieldCategory.Policies,
      id,
      commentField: ProposalField.ClientPolicies,
    });
  }

  public static VendorPolicy(id: Guid): ProposalField {
    return new ProposalField({
      name: ProposalFieldName.VendorPolicies,
      category: ProposalFieldCategory.Policies,
      id,
      commentField: ProposalField.VendorPolicies,
    });
  }

  public readonly id?: Guid;
  public readonly name: (typeof ProposalFieldName)[keyof typeof ProposalFieldName];
  public readonly category: (typeof ProposalFieldCategory)[keyof typeof ProposalFieldCategory];
  public readonly fromJSON: (json: JSONType) => IRedlineableField | null | undefined;
  public readonly proposalJSONKeys: string[];
  public readonly unresolvedFieldNames: ProposalFieldNameValue[];

  public get cacheKey(): string {
    let keyBase = `${this.name}.${this.category}`;
    return this.id ? `${keyBase}.${this.id.value}` : keyBase;
  }
  public get commentField(): ProposalField {
    return this._commentField ?? this;
  }
  /**
   * One category of the fee schedule, as opposed to the schedule as a whole, which carries no id.
   *
   * The fee schedule is the finest-grained thing the negotiation models — a category is negotiated one
   * column at a time (#4845, #4847) — and it is the one collection whose items keep their own change
   * history rather than sharing the collection's. Asked in more than one place, so the question has one
   * answer: `proposal-field-unread.ts` counts and acknowledges a category's changes against the
   * category, and `proposal-traversal-comments-pane.ts` keeps the comments pane off the table its row
   * lives in.
   */
  public get isFeeScheduleCategory(): boolean {
    return this.category === ProposalFieldCategory.FeeSchedule && Boolean(this.id);
  }

  public constructor(args: {
    name: (typeof ProposalFieldName)[keyof typeof ProposalFieldName];
    category: (typeof ProposalFieldCategory)[keyof typeof ProposalFieldCategory];
    id?: Guid;
    fromJSON?: (json: JSONType) => IRedlineableField | null | undefined;
    commentField?: ProposalField;
    proposalJSONKeys?: readonly string[];
    unresolvedFieldNames?: readonly ProposalFieldNameValue[];
  }) {
    this.name = args.name;
    this.category = args.category;
    this.id = args.id;
    this.fromJSON = args.fromJSON ?? (() => undefined);
    this._commentField = args.commentField;
    this.proposalJSONKeys = [...(args.proposalJSONKeys ?? [args.name])];
    this.unresolvedFieldNames = [...(args.unresolvedFieldNames ?? [args.name])];
  }

  /**
   * This field's identity for `ProposalFieldTimelineDropdown`'s revert-history mechanism — see
   * {@link ProposalFieldTimelineIdentity}. The vendor-team leader is special-cased to `scalarWithId`
   * unconditionally, regardless of whether this particular instance carries an id (it does, for
   * comment/history scoping — see `ProposalField.TeamLeader`) — so a caller can never route it through
   * collection-item mode by forgetting an override, the way ROADMAP.md's item 3 describes.
   *
   * `options.itemId` overrides the derived collection-item id for a field whose canonical collection
   * identity is compound (a vendor-team member's `userId:legalEntityId`, not just the userId
   * `ProposalField.TeamMember` carries) — ignored for anything that is not already a collection item,
   * so it can never smuggle a scalar-with-id field into collection-item mode either.
   */
  public timelineIdentity(options?: { itemId?: string }): ProposalFieldTimelineIdentity {
    if (this.name === ProposalFieldName.TeamLeader) {
      return { kind: "scalarWithId", path: "/vendorTeam/leader", [proposalFieldTimelineIdentityBrand]: true };
    }
    if (this.id) {
      return {
        kind: "collectionItem",
        itemId: options?.itemId ?? this.id.value,
        [proposalFieldTimelineIdentityBrand]: true,
      };
    }
    return { kind: "scalar", [proposalFieldTimelineIdentityBrand]: true };
  }

  public toJSON(): ProposalFieldJSON {
    return {
      name: this.name,
      category: this.category,
      id: this.id?.toJSON(),
      commentField: this._commentField?.toJSON(),
    };
  }

  public isEqualTo(field?: ProposalField | null): boolean {
    if (!field) return false;
    if (this.name !== field.name) return false;
    if (this.category !== field.category) return false;

    // If either has an id, both must and they must match
    if (this.id || field.id) {
      return !!this.id && !!field.id && this.id.isEqualTo(field.id);
    }
    return true;
  }

  public toString(): string {
    return this.name.toString();
  }

  protected readonly _commentField?: ProposalField;
}

export type FreelyPatchableFields = {
  reviewers: ProposalReviewer[];
  teamTemplateIds: Guid[];
  feeScheduleTemplateIds: Guid[];
};
